'use client';
import { stripMarkdown, stripMathDelimiters } from '@magicschool/runes/core/strings/markdown/stripStrings';
import type React from 'react';
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { createUnlockedAudioElement } from '@/features/tts/preserveAudioUserGesture';
import { type Payloads, TTSService } from './services';
import { BrowserTTSProvider } from './services/browser';
import { StreamingTTSProvider } from './services/streaming';
import type { TTSOptions } from './services/types';
import { markdownTablesToSpeech } from './utils/markdownTablesToSpeech';
import { replaceMathEquations } from './utils/replaceMathEquations';
import { shouldUseBlobTtsPlayback } from './utils/shouldUseBlobTtsPlayback';

const preprocessTextForSpeech = (text: string): string => stripMarkdown(markdownTablesToSpeech(text));

type ContextType = {
  play: (text: string, opts?: TTSOptions) => Promise<void>;
  pause: () => void;
  resume: () => Promise<void>;
  stop: () => void;
  abort: () => void;
  reset: () => void;

  // Status
  isPlaying: boolean;
  isPaused: boolean;
  isReady: boolean;
  currentText: string | null;
  error: Error | null;

  // Progress info
  progress: number;
  duration: number | null;

  isPlayingText: (text?: string) => boolean;

  lang?: string;
  voice?: string;
  rate?: number;
  pitch?: number;
  volume?: number;

  setLang: (lang: string) => void;
  setVoice: (voice: string) => void;
  setRate: (rate: number) => void;
  setVolume: (vol: number) => void;
  isLoadedText: (text?: string) => boolean;
};

const TTSContext = createContext<ContextType | null>(null);
TTSContext.displayName = 'TTSContext';

interface TTSProviderProps {
  endpoint: string;
  defaultOpts?: TTSOptions;
  children: ReactNode;
}

export const TTSProvider: React.FC<TTSProviderProps> = ({ endpoint, defaultOpts = {}, children }) => {
  const ttsRef = useRef<TTSService>(new TTSService(new StreamingTTSProvider(endpoint, defaultOpts), new BrowserTTSProvider(defaultOpts)));
  const textMappingRef = useRef<Map<string, string>>(new Map());

  const [state, setState] = useState({
    isPlaying: false,
    isPaused: false,
    isReady: false,
    currentText: null as string | null,
    error: null as Error | null,
    settings: defaultOpts as TTSOptions,
    progress: 0,
    duration: null as number | null,
  });

  useEffect(() => {
    const svc = ttsRef.current;
    if (defaultOpts.voice !== undefined) svc.setVoice(defaultOpts.voice);
    if (defaultOpts.rate !== undefined) svc.setRate(defaultOpts.rate);
    if (defaultOpts.lang !== undefined) svc.setLang(defaultOpts.lang);
    if (defaultOpts.volume !== undefined) svc.setVolume(defaultOpts.volume);
  }, [defaultOpts.voice, defaultOpts.rate, defaultOpts.lang, defaultOpts.volume]);

  useEffect(() => {
    // Debug
    const svc = ttsRef.current;
    window.MSTTS = svc;

    const onPlay = ({ text }: Payloads['play']) => {
      setState((s) => ({
        ...s,
        isPlaying: true,
        isPaused: false,
        currentText: text,
        isReady: s.currentText === text,
        error: null,
        progress: 0,
        duration: null,
      }));
    };

    const onPause = () => {
      setState((s) => ({ ...s, isPaused: true, isPlaying: false }));
    };

    const onResume = () => {
      setState((s) => ({ ...s, isPaused: false, isPlaying: true }));
    };

    const onStop = () => {
      setState((s) => ({
        ...s,
        isPlaying: false,
        isPaused: false,
        isReady: false,
        currentText: null,
        progress: 0,
        duration: null,
      }));
    };

    const onReset = () => {
      setState((s) => ({
        ...s,
        isPlaying: false,
        isPaused: false,
        progress: 0,
      }));
    };

    const onEnded = () => {
      setState((s) => ({
        ...s,
        isPlaying: false,
        isPaused: false,
        progress: s.duration ?? 0,
      }));
    };

    const onError = ({ error }: Payloads['error']) => {
      setState((s) => ({
        ...s,
        error,
        isReady: false,
      }));
    };

    const onAbort = () => {
      setState((s) => ({
        ...s,
        isPlaying: false,
        isPaused: false,
        isReady: false,
        currentText: null,
        progress: 0,
      }));
    };

    const onSettings = (opts: TTSOptions) => {
      setState((s) => ({ ...s, settings: opts }));
    };

    // Progress events
    const onProgress = ({ progress, duration }: Payloads['progress']) => {
      setState((s) => ({ ...s, progress, duration }));
    };

    const onReady = () => {
      setState((s) => ({
        ...s,
        isReady: true,
      }));
    };

    // Register all event handlers
    svc.on('play', onPlay);
    svc.on('pause', onPause);
    svc.on('resume', onResume);
    svc.on('stop', onStop);
    svc.on('reset', onReset);
    svc.on('ended', onEnded);
    svc.on('error', onError);
    svc.on('abort', onAbort);
    svc.on('settings', onSettings);
    svc.on('progress', onProgress);
    svc.on('ready', onReady);

    const handleUnload = () => {
      svc.stop();
      svc.reset();
      svc.clearCache();
    };

    window.addEventListener('beforeunload', handleUnload);

    // Cleanup
    return () => {
      svc.off('play', onPlay);
      svc.off('pause', onPause);
      svc.off('resume', onResume);
      svc.off('stop', onStop);
      svc.off('reset', onReset);
      svc.off('ended', onEnded);
      svc.off('error', onError);
      svc.off('abort', onAbort);
      svc.off('settings', onSettings);
      svc.off('progress', onProgress);
      svc.off('ready', onReady);

      handleUnload();
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  const play = async (text: string, opts?: TTSOptions) => {
    // iOS WebKit requires an audio element unlocked synchronously during the user
    // gesture — before ANY await (including math preprocessing / TTS fetch).
    const playOpts: TTSOptions = { ...opts };
    if (shouldUseBlobTtsPlayback() && !playOpts.gestureAudio) {
      playOpts.gestureAudio = createUnlockedAudioElement(playOpts.volume ?? 1);
    }

    let textToPlay = preprocessTextForSpeech(text);

    // Async math preprocessing loads external CDN scripts and breaks iOS gesture
    // chains. On blob-playback devices, use the synchronous delimiter stripper instead.
    if (shouldUseBlobTtsPlayback()) {
      textToPlay = stripMathDelimiters(textToPlay);
    } else {
      textToPlay = await replaceMathEquations(textToPlay);
    }

    textMappingRef.current.set(text, textToPlay);

    return ttsRef.current?.play(textToPlay, playOpts);
  };

  const pause = () => ttsRef.current?.pause();
  const resume = () => ttsRef.current?.resume() ?? Promise.resolve();
  const stop = () => ttsRef.current?.stop();
  const abort = () => ttsRef.current?.abort();
  const reset = () => ttsRef.current?.reset();
  const isPlayingText = (t?: string) => {
    if (!t) {
      return ttsRef.current?.isPlaying() ?? false;
    }

    const processedText = textMappingRef.current.get(t);
    if (processedText) {
      return ttsRef.current?.isPlaying(processedText) ?? false;
    }

    let processedT = preprocessTextForSpeech(t);
    processedT = stripMathDelimiters(processedT);
    return ttsRef.current?.isPlaying(processedT) ?? false;
  };
  const setLang = (l: string) => ttsRef.current?.setLang?.(l);
  const setVoice = (v: string) => ttsRef.current?.setVoice?.(v);
  const setRate = (r: number) => ttsRef.current?.setRate?.(r);
  const setVolume = (v: number) => ttsRef.current?.setVolume?.(v);

  const isLoadedText = (t?: string) => {
    if (!t) return false;
    const processedText = textMappingRef.current.get(t);
    if (processedText) {
      return processedText === currentText;
    }
    let processedT = preprocessTextForSpeech(t);
    processedT = stripMathDelimiters(processedT);
    return processedT === currentText;
  };

  const { isPlaying, isPaused, isReady, currentText, error, settings, progress, duration } = state;

  return (
    <TTSContext.Provider
      value={{
        play,
        pause,
        resume,
        stop,
        reset,
        abort,
        isPlaying,
        isPaused,
        isReady,
        currentText,
        error,
        progress,
        duration,
        isPlayingText,
        isLoadedText,
        lang: settings.lang,
        voice: settings.voice,
        rate: settings.rate,
        pitch: settings.pitch,
        volume: settings.volume,
        setLang,
        setVoice,
        setRate,
        setVolume,
      }}
    >
      {children}
    </TTSContext.Provider>
  );
};

TTSProvider.displayName = 'TTSProvider';

export const useTTS = (): ContextType => {
  const ctx = useContext(TTSContext);
  if (!ctx) throw new Error('useTTS must be inside a TTSProvider');
  return ctx;
};
